import {Component, OnInit} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {distinctUntilChanged, startWith, switchMap, tap} from 'rxjs';
import {
  IAllAudit,
  IAuditTable,
  ISystemLogsUser,
} from 'src/app/interfaces/system-logs.interface';
import {DialogService} from 'src/app/services/dialog.service';
import {SystemLogsService} from 'src/app/services/system-logs.service';
import {
  columnTypes,
  ITableRowAction,
  UITableHeader,
} from 'src/app/shared/ui-kits/table/table.models';
import {Utils} from 'src/app/shared/utils/utils';
import {SystemChangesPopupComponent} from '../system-changes-popup/system-changes-popup.component';
import {ToastrService} from 'ngx-toastr';

@UntilDestroy()
@Component({
  selector: 'app-audit',
  templateUrl: './audit.component.html',
  styleUrls: ['./audit.component.scss'],
})
export class AuditComponent implements OnInit {
  public tableData: IAuditTable[] = [];
  public auditAllHeaders: UITableHeader[] = [
    {key: 'dataStampUTC', index: 0, type: columnTypes.dateUTC},
    {key: 'actorRole', index: 1},
    {key: 'employeeName', index: 9},
    {key: 'actionType', index: 2},
    {key: 'resourceName', index: 3},
    {key: 'resourceId', index: 4, notVisible: true},
    {key: 'ipAddress', index: 5},
    {key: 'agentSoftware', index: 6},
    {key: 'agentHardware', index: 7},
    {key: 'changeValues', index: 8, type: columnTypes.valueDisplay},
  ];
  date = new UntypedFormControl([new Date()]);
  countOfItems!: number;
  selectedDate: string = '';
  public currentPage: number = 1;
  public exportData: IAllAudit[] = [];
  public formatDate = Utils.formatDateToString;
  public isDateChanged!: boolean;
  public actorIds: number[] = [];
  public employeeNames: ISystemLogsUser[] = [];

  constructor(
    private dialogService: DialogService,
    private systemLogsService: SystemLogsService,
    private notifier: ToastrService
  ) {}

  ngOnInit(): void {
    this.selectedDate = this.formatDate(this.date.value) || '';

    this.date.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        startWith(this.selectedDate),
        switchMap(date => {
          this.isDateChanged = false;
          this.selectedDate = date;
          return this.getEmployeeNames(date);
        }),
        switchMap(() =>
          this.getItemsCountWithData(1, this.selectedDate, this.actorIds)
        )
      )
      .subscribe(() => (this.isDateChanged = true));
  }

  openValuesChanges(event: ITableRowAction) {
    if (!event.additionalIndex) {
      return;
    }
    const oldValues =
      this.tableData[event.index].data[event.additionalIndex].oldValues;
    const newValues =
      this.tableData[event.index].data[event.additionalIndex].newValues;
    if (!oldValues || !newValues) {
      this.notifier.error('Not able to view the following log');
      return;
    }

    this.dialogService.open(SystemChangesPopupComponent, {
      data: this.tableData[event.index].data[event.additionalIndex],
      settings: {},
    });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.getLogData(this.currentPage, this.selectedDate, this.actorIds)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  getLogData(page: number, date: string, actorIds?: number[]) {
    return this.systemLogsService.getAllAudit({page, date, actorIds}).pipe(
      tap(logs => {
        this.tableData = logs;
        this.generateExportData();
      })
    );
  }

  getItemsCountWithData(page: number, date: string, actorIds?: number[]) {
    return this.systemLogsService.getCountOfItems({page, date, actorIds}).pipe(
      untilDestroyed(this),
      switchMap(count => {
        this.countOfItems = count;
        return this.getLogData(page, this.selectedDate, this.actorIds);
      })
    );
  }

  generateExportData() {
    this.exportData = [];
    this.tableData.forEach(item => this.exportData.push(...item.data));
  }

  getEmployeeNames(date: string) {
    return this.systemLogsService.getEmployeeNames(date).pipe(
      untilDestroyed(this),
      tap(res => {
        this.employeeNames = res;
        for (const user of this.employeeNames) {
          user.selected = this.actorIds.includes(user.id);
        }
        this.defineCheckedUsers();
      })
    );
  }

  handlingCheckboxesChanges(): void {
    this.defineCheckedUsers();
    this.isDateChanged = false;
    this.getItemsCountWithData(1, this.selectedDate, this.actorIds).subscribe(
      () => (this.isDateChanged = true)
    );
  }

  defineCheckedUsers(): void {
    this.actorIds = this.employeeNames.filter(v => v.selected).map(v => v.id);
  }
}
